/**
 * Practice Upgrade Modal
 *
 * Shown when prompting users to upgrade to the Practice (PRO_PLUS) plan.
 * Uses HeidiUpgradeBaseModal with variant='practice'.
 *
 * Figma: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=7454-175300
 * Trial-recap feature list: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=10266-38745
 */

import { usePracticeUpgradeModal } from '../hooks/usePracticeUpgradeModal';
import { UpgradeTeamAccessSvg } from './upgrade-team-access-svg';

import { openTeamTrialUpgradeRequestModalAtom } from '../../../trials-and-upgrades/team-trial-upgrade-request-modal/atoms';
import { trialRecapModalMessages } from '../../../trials-and-upgrades/trial-recap-modal/messages';

import { Period } from '@heidi/commerce/contracts';
import { trackUpgradeToPracticeModalTeamUpgradeRequestSubmit } from '@heidi/commerce/data';
import { Button } from '@heidi/design-system/button';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import { TierPracticeFilled } from '@heidi/design-system/icons';
import { Link } from '@heidi/design-system/link';
import { TypographyH2, TypographyP2 } from '@heidi/design-system/typography';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useSetAtom } from 'jotai';
import { Loader2 } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ClinicianTrialRecapTestimonial } from '../../../trials-and-upgrades/clinician-trial-recap-testimonial/clinician-trial-recap-testimonial';
import { resolveClinicianTrialRecapTestimonialVariant } from '../../../trials-and-upgrades/clinician-trial-recap-testimonial/clinician-trial-recap-testimonial.utils';
import { UpgradeTermsFooter } from '../../../trials-and-upgrades/ninety-day-end-of-trial/upgrade-terms-footer';
import { UpgradeTrialFeatureList } from '../../../trials-and-upgrades/ninety-day-end-of-trial/upgrade-trial-feature-list';
import { TeamTrialUpgradeRequestButton } from '../../../trials-and-upgrades/team-trial-upgrade-request-button/team-trial-upgrade-request-button';
import { trialUpgradeRequestCallbackModalMessages } from '../../../trials-and-upgrades/trial-upgrade-request-callback-modal/messages';

import { getCommerceBillingUiPorts } from '../billing-ui-ports';
import { portComponent } from '../port-component';
import { practiceUpgradeRecapMessages } from './practice-upgrade-recap-messages';
const AppleIapInfoText = portComponent('AppleIapInfoText');
const HeidiUpgradeBaseModal = portComponent('HeidiUpgradeBaseModal');
const PriceDisplay = portComponent('PriceDisplay');

interface PracticeUpgradeModalProps {
  /** When provided, modal is controlled by launch flow */
  launchOpen?: boolean;
  onLaunchClose?: () => void;
}

export const PracticeUpgradeModal = ({
  launchOpen,
  onLaunchClose,
}: PracticeUpgradeModalProps = {}) => {
  const isHeidi2PricingPlansModalEnabled =
    getCommerceBillingUiPorts().useIsHeidi2PricingPlansModalEnabled();
  const {
    isOpen,
    handleOpenChange,
    handleClose,
    handleUpgradeClick,
    handleTalkToUsClick,
    handleExtendTrial,
    handleViewAllPlans,
    isUpgradeDisabled,
    upgradeDisabledTooltip,
    titleText,
    subtitleText,
    effectiveDaysLeft,
    showNinetyDayTrialVariant,
    showNonAdminRequestBanner,
    featuresTitle,
    features,
    practiceProduct,
    isPending,
    showExtendTrialButton,
    footerText,
    showContactSalesInsteadOfUpgrade,
    handleContactSalesClick,
    hasCallbackRequested,
    isFromTrialRecap,
    isNinetyDayTrialEnded,
    planChangesArticleUrl,
  } = usePracticeUpgradeModal({ launchOpen, onLaunchClose });
  const openTeamTrialUpgradeRequestModal = useSetAtom(
    openTeamTrialUpgradeRequestModalAtom
  );
  const intl = useIntl();
  const region = getCommerceBillingUiPorts().useRegion();
  const { data: user } = getCommerceBillingUiPorts().useQueryUser();
  const variant = resolveClinicianTrialRecapTestimonialVariant(region, user?.country);
  const termsHref = getCommerceBillingUiPorts().getTermsOfUseLink(intl.locale);

  return (
    <HeidiUpgradeBaseModal
      variant="practice"
      yearlyOnly
      hideTestimonial={isFromTrialRecap}
      hidePricing={showNonAdminRequestBanner}
      pricingFeaturesGroupedCard={showNinetyDayTrialVariant}
      isUpgradeDisabled={isUpgradeDisabled}
      upgradeDisabledTooltip={upgradeDisabledTooltip}
      title={
        <div className="flex flex-col gap-2">
          {isFromTrialRecap && <FramedIcon state="practice" className="mx-auto mb-2" />}
          <TypographyH2 className="text-text-primary">
            {isFromTrialRecap ? (
              <FormattedMessage {...practiceUpgradeRecapMessages.upgradingTitle} />
            ) : isNinetyDayTrialEnded ? (
              <FormattedMessage
                {...practiceUpgradeRecapMessages.titleNinetyDayTrialEnded}
              />
            ) : showNinetyDayTrialVariant ? (
              <FormattedMessage
                {...practiceUpgradeRecapMessages.title}
                values={{ daysLeft: effectiveDaysLeft }}
              />
            ) : (
              titleText
            )}
          </TypographyH2>
          {!isFromTrialRecap && (
            <TypographyP2 className="text-text-secondary">
              {isNinetyDayTrialEnded ? (
                <FormattedMessage
                  {...practiceUpgradeRecapMessages.subtitleNinetyDayTrialEnded}
                />
              ) : showNinetyDayTrialVariant ? (
                <FormattedMessage
                  {...(showNonAdminRequestBanner
                    ? practiceUpgradeRecapMessages.subtitleNonAdminTeam
                    : practiceUpgradeRecapMessages.subtitle)}
                  values={{
                    link: (chunks) => (
                      <Link
                        variant="inline"
                        tone="default"
                        href={planChangesArticleUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {chunks}
                      </Link>
                    ),
                  }}
                />
              ) : (
                subtitleText
              )}
            </TypographyP2>
          )}
        </div>
      }
      featuresTitle={showNinetyDayTrialVariant ? undefined : featuresTitle}
      features={showNinetyDayTrialVariant ? undefined : features}
      featuresContent={
        showNinetyDayTrialVariant ? (
          <UpgradeTrialFeatureList tier={SubscriptionTier.CLINICIAN_PRO_PLUS} />
        ) : undefined
      }
      yearlyContent={
        practiceProduct ? (
          <PriceDisplay period={Period.ONE_YEAR} product={practiceProduct} />
        ) : null
      }
      rightPanelContent={
        showNinetyDayTrialVariant ? (
          <ClinicianTrialRecapTestimonial variant={variant} />
        ) : (
          <div>
            <UpgradeTeamAccessSvg className="h-auto w-full" />
          </div>
        )
      }
      primaryUpgradeButtonContent={
        <>
          <TierPracticeFilled className="shrink-0" aria-hidden />
          {isPending ? (
            <Loader2 className="animate-spin" />
          ) : isNinetyDayTrialEnded ? (
            <FormattedMessage
              defaultMessage="Upgrade to Practice"
              id="kOKn5EppyP"
              description="Button upgrading to the Practice plan"
            />
          ) : showNinetyDayTrialVariant ? (
            <FormattedMessage {...practiceUpgradeRecapMessages.upgradeCta} />
          ) : (
            <FormattedMessage
              defaultMessage="Upgrade now"
              id="nRZty7WTU/"
              description="Button starting a plan upgrade"
            />
          )}
        </>
      }
      primaryButtonTestId="billing-practice-upgrade-confirm-button"
      onUpgradeClick={(period: Period) => void handleUpgradeClick(period)}
      secondaryButton={
        showNinetyDayTrialVariant && !showContactSalesInsteadOfUpgrade ? (
          <Button
            variant="outline"
            size="md"
            className="w-full"
            onClick={handleTalkToUsClick}
          >
            <FormattedMessage
              defaultMessage="Talk to us"
              id="o32MA/Um11"
              description="Button linking to the sales contact page"
            />
          </Button>
        ) : showExtendTrialButton ? (
          <Button
            variant="outline"
            size="md"
            className="w-full"
            onClick={handleExtendTrial}
          >
            <FormattedMessage
              defaultMessage="Extend my trial"
              id="HosDQnTnYn"
              description="Button extending the current trial"
            />
          </Button>
        ) : isHeidi2PricingPlansModalEnabled ? (
          <Button
            variant="ghost"
            size="md"
            className="w-full"
            onClick={handleViewAllPlans}
          >
            <FormattedMessage
              defaultMessage="View all plans"
              id="H+YOpMRHo4"
              description="Button opening the full pricing plans view"
            />
          </Button>
        ) : undefined
      }
      isUpgradePending={isPending}
      footerText={
        showNonAdminRequestBanner ? undefined : showNinetyDayTrialVariant ? (
          <UpgradeTermsFooter
            termsHref={termsHref}
            privacyHref={getCommerceBillingUiPorts().USAGE_POLICY}
          />
        ) : (
          footerText
        )
      }
      footerCaptionClassName={
        showNonAdminRequestBanner
          ? undefined
          : showNinetyDayTrialVariant
            ? 'text-center'
            : undefined
      }
      primaryCtaReplacement={
        getCommerceBillingUiPorts().isAppleCompliantBuild()
          ? () => <AppleIapInfoText className="text-center text-text-secondary" />
          : showNonAdminRequestBanner
            ? (assignPrimaryButtonRef: (el: HTMLButtonElement | null) => void) => (
                <TeamTrialUpgradeRequestButton
                  ref={(el: HTMLButtonElement | null) => {
                    assignPrimaryButtonRef(el);
                  }}
                  plan={SubscriptionTier.CLINICIAN_PRO_PLUS}
                  onOpenTeamRequestModal={() => {
                    handleClose();
                    openTeamTrialUpgradeRequestModal({
                      plan: SubscriptionTier.CLINICIAN_PRO_PLUS,
                      trackRequestSubmitted:
                        trackUpgradeToPracticeModalTeamUpgradeRequestSubmit,
                      onRequestSent: () => {
                        handleClose();
                      },
                      onUserDismiss: () => {
                        handleOpenChange(true);
                      },
                    });
                  }}
                />
              )
            : showContactSalesInsteadOfUpgrade
              ? (assignPrimaryButtonRef: (el: HTMLButtonElement | null) => void) => (
                  <Button
                    ref={(el: HTMLButtonElement | null) => {
                      assignPrimaryButtonRef(el);
                    }}
                    type="button"
                    variant="pro_plus"
                    size="md"
                    disabled={hasCallbackRequested}
                    onClick={handleContactSalesClick}
                    className="w-full"
                  >
                    {!hasCallbackRequested && (
                      <TierPracticeFilled className="shrink-0" aria-hidden />
                    )}
                    <FormattedMessage
                      {...(hasCallbackRequested
                        ? trialUpgradeRequestCallbackModalMessages.callbackRequestedCta
                        : trialRecapModalMessages.talkToUsKeepPlanCtaPractice)}
                    />
                  </Button>
                )
              : undefined
      }
      open={isOpen}
      onOpenChange={handleOpenChange}
    />
  );
};
